import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { User } from "../types";
import { getConfigs } from "../configs/env";
import { decodeToken } from "../libs/token";
import { extractDotNetErrorMessage } from "../api/client";
import { getInfoUserAuth } from "../services/auth";

interface AuthContextType {
  user: User | null;
  language: "es" | "ca";
  setLanguage: (lang: "es" | "ca") => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  translations: Record<string, string>;
  isLoading: boolean;
  token: string | null;
  role: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const translations = {
  es: {
    // Navigation
    entitiesNav: "Entidades y Usuarios",
    dashboard: "Panel de control",
    surveys: "Encuestas",
    genericQuestions: "Preguntas genéricas",
    specificQuestions: "Preguntas específicas",
    codes: "Códigos",
    downloadData: "Descargar datos",
    logout: "Cerrar sesión",

    // Dashboard
    hello: "¡Hola! Echa un vistazo general!.",
    helloAdmin: "¡Hola! Echa un vistazo general",
    helloOrganization: "¡Hola! Echa un vistazo general a tu entidad",
    role: "Rol",
    Administrator: "Administrador",
    organization: "Organización",
    totalSurveys: "Total de encuestas",
    activeEntities: "Entidades activas",
    totalResponses: "Total de respuestas",
    activeCodes: "Códigos activos",
    inactiveCodes: "Códigos inactivos",
    ownSurveys: "Encuestas propias",
    ownResponses: "Respuestas registradas",
    codeStatus: "Estado del código",
    lastPublished: "Última encuesta publicada",
    surveysReachedTarget: "Encuestas que alcanzaron objetivo",
    surveysReachedTargetPercentage: "% encuestas que alcanzaron objetivo",
    surveyTargetsTitle: "Progreso de objetivos por encuesta",
    searchSurveysLocal: "Buscar encuestas...",
    targetRequiredLabel: "Objetivo",
    completedLabel: "Completadas",
    ofLabel: "de",
    // New KPIs
    questionsAudioTitle: "Preguntas con/sin audio",
    questionsWithAudio: "Con audio",
    questionsWithoutAudio: "Sin audio",
    surveyTypesTitle: "Tipos de encuestas",
    genericSurveys: "Genéricas",
    specificSurveys: "Específicas",

    // Quick Actions
    quickActions: "Acciones rápidas",
    createSurvey: "Crear encuesta",
    editGenericQuestions: "Editar preguntas genéricas",
    manageCodes: "Gestionar códigos",
    downloadResponses: "Descargar respuestas",
    yesDelete: "Sí, eliminar",

    // Recent Activity
    recentActivity: "Actividad reciente",
    noActivity: "No hay actividad reciente",

    // Surveys
    newSurvey: "Nueva encuesta",
    title: "Título",
    description: "Descripción",
    type: "Tipo",
    generic: "Genérica",
    entity: "Entidad",
    defaultLanguage: "Idioma por defecto",
    status: "Estado",
    actions: "Acciones",
    noSurveys: "No hay encuestas",
    startANewSurvey: "Comienza creando tu primera encuesta.",
    manageSurveys: "Gestiona las encuestas de la plataforma",
    draft: "Borrador",
    published: "Publicado",
    archived: "Archivado",
    loadingSurveys: "Cargando ...",
    errorLoadingSurveys: "Error al cargar las encuestas",
    unexpectedError: "Ha ocurrido un error inesperado",
    retry: "Reintentar",
    searchSurveys: "Buscar encuestas...",
    allTypes: "Todos los tipos",
    allStatuses: "Todos los estados",
    organizationColumn: "Organización",
    questions: "preguntas",
    questionsOrdered:
      "Preguntas ordenadas (genéricas primero, luego por orden):",
    genericQuestion: "Genérica",
    specificQuestion: "Específica",
    originalOrder: "Orden original",
    errorDeletingSurvey: "Error al eliminar encuesta",
    surveyDeletedSuccessfully: "Encuesta eliminada exitosamente",
    modifyData: "Modifica los datos de la encuesta",
    completeInfo: "Completa la información para crear una nueva encuesta",
    spanish: "Español",
    catalan: "Català",
    required: "*",
    generateTranslation: "Generar traducción automáticamente",
    translating: "Traduciendo...",
    selectEntity: "Selecciona la entidad",
    noEntitiesAvailable: "No existen entidades disponibles en este momento.",
    createNewEntity: "Crear nueva entidad",
    noEntitiesOrganization:
      "No existen entidades disponibles. Por favor, solicita a un administrador que cree una entidad para ti.",
    selectAnEntity: "Selecciona una entidad",
    surveyOrientation: "Orientación de la encuesta",
    child: "Niño",
    family: "Familia",
    surveyGoal: "Objetivo por encuesta",
    enterGoal: "Introduce el objetivo (número)",
    state: "Estado",
    update: "Actualizar",
    create: "Crear",
    survey: "Encuesta",
    errorCreatingUpdating: "Error",
    creating: "creando",
    updating: "actualizando",
    surveyCreatedUpdated: "Encuesta",
    correctly: "correctamente",
    created: "creada",
    updated: "actualizada",
    redirectingEntities: "Redirigiendo a la creación de entidades...",
    titleSpanishRequired: "El título en español es obligatorio",
    titleCatalanRequired: "El título en catalán es obligatorio",
    descriptionSpanishRequired: "La descripción en español es obligatorio",
    descriptionCatalanRequired: "La descripción en catalán es obligatorio",
    mustSelectEntity: "Debe seleccionar una entidad",
    goalMustBeGreater: "El objetivo debe ser mayor a 0",
    goalRequired: "El objetivo es obligatorio",
    modifySurveyData: "Modifica los datos de la encuesta",
    completeSurveyInfo: "Completa la información para crear una nueva encuesta",
    surveyType: "Tipo de encuesta",
    selectSurveyType: "Selecciona el tipo de encuesta",
    genericSurveyDescription: "Encuesta genérica para todas las entidades",
    entitySurveyDescription: "Encuesta específica para una entidad",
    genericSurveyCodeInfo: "Se generará un código único para cada entidad activa en el sistema",
    specificSurveyCodeInfo: "Se generará un solo código para la entidad seleccionada:",
    selectEntityToSeeCodeInfo: "Selecciona una entidad para ver la información del código",
    genericSurveyEditWarning: "Los cambios afectarán a todas las entidades activas en el sistema",
    specificSurveyEditWarning: "Los cambios afectarán a la entidad seleccionada",
    surveyMustHaveQuestions: "La encuesta debe tener al menos una pregunta",
    genericSurveyOnlyGenericQuestions: "Las encuestas genéricas solo pueden contener preguntas genéricas",
    surveyCode: "Código de la encuesta",
    surveyCodeActivation: "Activación del código de la encuesta",
    surveyCodeActive: "Activado",
    surveyCodeDeActive: "Desactivado",
    titleSpanish: "Escribe un título en español",
    titleCatalan: "Escribe un título en catalán",
    descriptionSpanish: "Escribe una descripción en español",
    descriptionCatalan: "Escribe una descripción en catalán",
    errorCreatingSurvey: "Error al crear la encuesta",
    errorUpdatingQuestions:
      "Sin embargo, hubo un problema actualizando algunas preguntas. Por favor, verifica la asignación de preguntas.",
    loadingEntities: "Cargando entidades...",
    loadingUsers: "Cargando usuarios...",

    // Questions
    question: "Pregunta",
    genericShort: "Genérica",
    specificShort: "Específica",
    surveyId: "Asignar a la encuesta:",
    typeQuestions: "Tipo de pregunta",
    addQuestion: "Añadir pregunta",
    questionText: "Texto de la pregunta",
    audioUrl: "URL de audio (opcional)",
    noQuestion: "Esta encuesta no tiene preguntas configuradas.",
    questionsSection: "Preguntas de la encuesta",
    availableQuestions: "Preguntas disponibles",
    selectedQuestions: "Preguntas seleccionadas",
    addToSurvey: "Añadir a la encuesta",
    removeFromSurvey: "Quitar de la encuesta",
    noQuestionsAvailable: "No hay preguntas disponibles",
    loadingQuestions: "Cargando preguntas...",
    questionOrder: "Orden",
    reorderQuestions: "Reordenar preguntas",
    moveUp: "Subir",
    moveDown: "Bajar",
    manageGenericQuestions:
      "Gestiona las preguntas genéricas que pueden usar todas las organizaciones",
    manageSpecificQuestions:
      "Gestiona las preguntas específicas que solo pueden usar algunas organizaciones",
    aboutSpecificQuestions: "Sobre las preguntas específicas",
    specificQuestionsArePerSurvey:
      "Las preguntas específicas pertenecen a una encuesta concreta.",
    onlyOwnersOrAdminsCanEditSpecific:
      "Solo propietarios o administradores pueden editar preguntas específicas.",
    noSpecificQuestions: "No hay preguntas específicas",
    startAddingFirstSpecificQuestion:
      "Empieza creando tu primera pregunta específica",
    audioAvailable: "Audio disponible",
    moveUp1: "Mover arriba",
    moveDown1: "Mover abajo",
    noGenericQuestions: "No hay preguntas genéricas",
    startAddingFirstGenericQuestion:
      "Comienza añadiendo tu primera pregunta genérica",
    aboutGenericQuestions: "Sobre las preguntas genéricas",
    genericQuestionsAvailable:
      "Las preguntas genéricas están disponibles para todas las organizaciones",
    organizationsCanAdd:
      "Las organizaciones pueden añadir estas preguntas a sus encuestas",
    onlyAdminsCanCreateGeneric:
      "Solo los administradores pueden crear y editar preguntas genéricas",
    eachQuestionMustHaveTranslations:
      "Cada pregunta debe tener traducción en español y catalán",
    canAddAudioForAccessibility:
      "Puedes añadir audio opcional para mejorar la accesibilidad",
    ratingScale: "Escala de valoración",
    audioEnabled: "Audio: Activado/Desactivado",
    audioEnabledDescription:
      "Activa esta opción para habilitar audio para esta pregunta",
    audioFile: "Subir archivo de audio",
    audioFileUploaded: "Se ha subido un archivo de audio personalizado",
    audioWillBeGenerated:
      "Se generará automáticamente un audio para cada idioma",
    audioFromFile: "Audio: Archivo subido por el usuario",
    autoGeneratedAudio: "Audio: Generado automáticamente",
    noTextForAudio: "No hay texto disponible para generar audio",
    audioPlaybackFailed: "Error al reproducir el audio",
    regenerateAudio: "Regenerar audio",
    audioES: "Audio en Español",
    audioCA: "Audio en Catalán",
    audioEN: "Audio en Inglés",
    audioAR: "Audio en Árabe",
    es: "Español",
    ca: "Catalán",
    english: "Inglés",
    arabic: "Árabe",
    titleEnglish: "Escribe un título en inglés",
    titleArabic: "Escribe un título en árabe",
    descriptionEnglish: "Escribe una descripción en inglés",
    descriptionArabic: "Escribe una descripción en árabe",
    generatingAudio: "Generando audio...",
    generatingAudios: "Generando audios...",
    preferUploadAudio: "Preferir subir audio",
    provideAudioUrl: "Prefiero proporcionar una URL de audio",
    noAudioAvailable: "Audio no disponible",
    needsRegeneration: "Necesita regeneración",
    play: "Reproducir",
    pause: "Pausar",
    // Codes
    code: "Código",
    active: "Activo",
    inactive: "Inactivo",
    activate: "Activar",
    deactivate: "Desactivar",
    copyCode: "Copiar código",
    manageSurveyCodes: "Gestiona los códigos de acceso a las encuestas",
    surveyToggleStatusSuccessfully:
      "Estado de la encuesta actualizado exitosamente",
    errorUpdatingSurveyStatus: "Error al actualizar el estado de la encuesta",
    // Add these to your translations.es object
    noCodes: "No hay códigos",
    codesGeneratedAutomatically:
      "Los códigos se generan automáticamente al crear encuestas",
    accessCodeRules: "Reglas de códigos de acceso",
    updatingCode: "Actualizando el código de acceso...",
    onlyOneActiveCodePerSurvey:
      "Solo puede haber un código activo por encuesta",
    codesGeneratedWhenCreatingSurvey:
      "Los códigos se generan automáticamente al crear una encuesta",
    canToggleCodesAsNeeded: "Puedes activar/desactivar códigos según necesites",
    inactiveCodesDenyAccess:
      "Los códigos desactivados no permiten acceso a la encuesta",
    // Export
    exportData: "Exportar datos",
    selectSurvey: "Seleccionar encuesta",
    responseType: "Tipo de respuesta",
    childResponse: "Niño/a",
    familyResponse: "Familia",
    dateRange: "Rango de fechas",
    exportToExcel: "Exportar a Excel",

    // Common
    close: "Cerrar",
    save: "Guardar",
    saving: "Guardando...",
    savingQuestion: "Guardando pregunta...",
    cancel: "Cancelar",
    edit: "Editar",
    delete: "Eliminar",
    date: "Fecha",
    createdAt: "Fecha de creación",
    updatedAt: "Última actualización",
    never: "Nunca",
    viewDetails: "Ver detalles",
    search: "Buscar",
    searchQuestions: "Buscar preguntas...",
    defaultOrder: "Orden por defecto",
    newestFirst: "Más recientes primero",
    oldestFirst: "Más antiguas primero",
    allQuestions: "Todas las preguntas",
    updateAlone: "Actualización",
    creatingAlone: "Creación",
    modifyingData: "Modifica los datos",
    completingInformation: "Completa la información",
    ofThe: "de la",
    the: "del",
    systemUser: "Sistema",
    //Entities
    entities: "Entidades",
    manageEntities: "Gestión de Entidades y Usuarios",
    manageEntitiesDescription:
      "Administra las organizaciones y usuarios de la plataforma",
    add: "Añadir",
    addEntity: "Nueva Entidad",
    editEntity: "Editar entidad",
    deleteEntity: "Eliminar entidad",
    entityName: "Nombre de la entidad",
    entityType: "Tipo de entidad",
    entityStatus: "Estado de la entidad",
    users: "Usuarios",
    user: "Usuario",
    manageUsers: "Gestionar usuarios",
    addUser: "Añadir usuario",
    editUser: "Editar usuario",
    deleteUser: "Eliminar usuario",
    userName: "Nombre de usuario",
    userEmail: "Correo electrónico",
    userRole: "Rol de usuario",
    assignToEntity: "Asignar a entidad",
    removeFromEntity: "Quitar de entidad",
    noEntities: "No hay entidades registradas.",
    noUsers: "No hay usuarios registrados.",
    confirmDeleteEntity: "¿Seguro que deseas eliminar esta entidad?",
    confirmDeleteUser: "¿Seguro que deseas eliminar este usuario?",
    entityCreated: "Entidad creada correctamente",
    entityUpdated: "Entidad actualizada correctamente",
    entityDeleted: "Entidad eliminada correctamente",
    userCreated: "Usuario creado correctamente",
    userUpdated: "Usuario actualizado correctamente",
    userDeleted: "Usuario eliminado correctamente",
    contactInfo: "Información de contacto",
    viewProfile: "Ver perfil",
    describeEntityMission:
      "Describe brevemente la misión y actividades de la entidad",
    phone: "Teléfono",
    address: "Dirección",
    addressPlaceholder: "Calle, número, ciudad",
    fullName: "Nombre completo",
    fullNamePlaceholder: "Nombres y apellidos",
    selectRole: "Selecciona un rol",
    onlyAdminsCanCreate:
      "Solo administradores pueden crear otros administradores",
    selectOrganization: "Seleccionar organización",
    contactEmail: "Correo electrónico de contacto",
    password: "Contraseña",
    confirmPassword: "Confirmar contraseña",
    passwordsDoNotMatch: "Las contraseñas no coinciden",
    minLength: "Mínimo 8 caracteres",
    repeatPassword: "Repite la contraseña",
    managementEntities: "Gestión de Entidades",
    managementUsers: "Gestión de Usuarios",
    entitiesDescription:
      "Las entidades representan las organizaciones que usan la plataforma",
    usersDescription: "Cada entidad puede tener múltiples usuarios asignados",
    activeEntitiesDescription:
      "Solo entidades activas pueden crear encuestas y acceder a datos",
    contactInfoDescription:
      "La información de contacto es importante para las comunicaciones",
    organizationUsersMustBeAssigned:
      "Los usuarios de organización deben estar asignados a una entidad",
    adminsHaveFullAccess:
      "Los administradores tienen acceso completo a la plataforma",
    inactiveUsersNoAccess:
      "Los usuarios inactivos no pueden acceder al sistema",
    editQuestion: "Editar pregunta",
    newGenericQuestion: "Nueva pregunta genérica",
    newSpecificQuestion: "Nueva pregunta específica",
    modifyExistingQuestion: "Modifica la pregunta existente",
    createNewGenericQuestion: "Crea una nueva pregunta genérica",
    createNewSpecificQuestion: "Crea una nueva pregunta específica",
    writeQuestionInSpanish: "Escribe la pregunta en español...",
    writeQuestionInCatalan: "Escriu la pregunta en català...",
    audioUrlDescription:
      "URL del archivo de audio para mejorar la accesibilidad (opcional)",
    questionOfOrder: "Orden de la pregunta",
    questionOrderDescription:
      "Orden en el que aparecerá la pregunta en las encuestas",
    preview: "Vista previa",
    noText: "Sin texto",
    audioConfigured: "Audio configurado",
    invalidAudioUrl: "URL de audio inválida",
    audioGenerated: "Audio generado",
    audioGeneratedSuccessfully:
      "El audio se ha generado correctamente desde la URL.",
    couldNotGenerateAudio: "No se pudo generar el audio desde la URL.",
    textSpanishRequired: "El texto de la pregunta en español es obligatorio",
    textCatalanRequired: "El texto de la pregunta en catalán es obligatorio",
    textArabicRequired: "El texto de la pregunta en árabe es obligatorio",
    textEnglishRequired: "El texto de la pregunta en inglés es obligatorio",
    //filtros
    allStates: "Todos los estados",
    activeFemale: "Activa",
    inactiveFemale: "Inactiva",

    //notifications
    actionCannotBeUndone: "Esta acción no se puede deshacer.",

    //validations
    nameRequired: "El nombre es obligatorio",
    emailRequired: "El correo electrónico es obligatorio",
    descriptionRequired: "La descripción es obligatoria",
    phoneRequired: "El teléfono es obligatorio",
    passwordRequired: "La contraseña es obligatoria",
    addressRequired: "La dirección es obligatoria",
    fullNameRequired: "El nombre completo es obligatorio",
    organizationIdRequired: "La organización es obligatoria",
    confirmPasswordRequired: "La confirmación de contraseña es obligatoria",
  },
  ca: {
    // Navigation
    entitiesNav: "Entitats i Usuaris",
    dashboard: "Tauler",
    surveys: "Enquestes",
    genericQuestions: "Preguntes genèriques",
    specificQuestions: "Preguntes específiques",
    codes: "Codis",
    downloadData: "Descarregar dades",
    logout: "Tancar sessió",

    // Dashboard
    hello: "Hola",
    helloAdmin: "Hola! Fes una ullada general",
    helloOrganization: "Hola! Fes una ullada general a la teva entitat",
    role: "Rol",
    Administrator: "Administrador",
    organization: "Organització",
    totalSurveys: "Total d'enquestes",
    activeEntities: "Entitats actives",
    totalResponses: "Total de respostes",
    activeCodes: "Codis actius",
    inactiveCodes: "Codis inactius",
    ownSurveys: "Enquestes pròpies",
    ownResponses: "Respostes registrades",
    codeStatus: "Estat del codi",
    lastPublished: "Última enquesta publicada",
    surveysReachedTarget: "Enquestes que han assolit l'objectiu",
    surveysReachedTargetPercentage: "% d'enquestes que han assolit l'objectiu",
    surveyTargetsTitle: "Progrés d'objectius per enquesta",
    searchSurveysLocal: "Cercar enquestes...",
    targetRequiredLabel: "Objectiu",
    completedLabel: "Completades",
    ofLabel: "de",
    // New KPIs
    questionsAudioTitle: "Preguntes amb/sense àudio",
    questionsWithAudio: "Amb àudio",
    questionsWithoutAudio: "Sense àudio",
    surveyTypesTitle: "Tipus d'enquestes",
    genericSurveys: "Genèriques",
    specificSurveys: "Específiques",

    // Quick Actions
    quickActions: "Accions ràpides",
    createSurvey: "Crear enquesta",
    editGenericQuestions: "Editar preguntes genèriques",
    manageCodes: "Gestionar codis",
    downloadResponses: "Descarregar respostes",
    yesDelete: "Sí, eliminar",

    // Recent Activity
    recentActivity: "Activitat recent",
    noActivity: "No hi ha activitat recent",

    // Surveys
    newSurvey: "Nova enquesta",
    title: "Títol",
    description: "Descripció",
    type: "Tipus",
    generic: "Genèrica",
    entity: "Entitat",
    defaultLanguage: "Idioma per defecte",
    status: "Estat",
    actions: "Accions",
    noSurveys: "No hi han enquestes",
    startANewSurvey: "Comença creant una nova enquesta.",
    manageSurveys: "Gestiona les enquestes de la plataforma",
    draft: "Esborrany",
    published: "Publicat",
    archived: "Arxivat",
    loadingSurveys: "Carregant enquestes...",
    errorLoadingSurveys: "Error en carregar les enquestes",
    unexpectedError: "Ha ocorregut un error inesperat",
    retry: "Tornar a intentar",
    searchSurveys: "Buscar enquestes...",
    allTypes: "Tots els tipus",
    allStatuses: "Tots els estats",
    organizationColumn: "Organització",
    questions: "preguntes",
    questionsOrdered:
      "Preguntes ordenades (genèriques primer, després per ordre):",
    genericQuestion: "Genèrica",
    specificQuestion: "Específica",
    originalOrder: "Ordre original",
    errorDeletingSurvey: "Error en eliminar enquesta",
    surveyDeletedSuccessfully: "Enquesta eliminada exitosament",
    modifyData: "Modifica les dades de l'enquesta",
    completeInfo: "Completa la informació per crear una nova enquesta",
    spanish: "Espanyol",
    catalan: "Català",
    required: "*",
    generateTranslation: "Generar traducció automàticament",
    translating: "Traduint...",
    selectEntity: "Selecciona l'entitat",
    noEntitiesAvailable: "No existeixen entitats disponibles en aquest moment.",
    createNewEntity: "Crear nova entitat",
    noEntitiesOrganization:
      "No existeixen entitats disponibles. Si us plau, demana a un administrador que creï una entitat per a tu.",
    selectAnEntity: "Selecciona una entitat",
    surveyOrientation: "Orientació de l'enquesta",
    child: "Nen",
    family: "Família",
    surveyGoal: "Objectiu per enquesta",
    enterGoal: "Introdueix l'objectiu (número)",
    state: "Estat",
    update: "Actualitzar",
    create: "Crear",
    survey: "Enquesta",
    errorCreatingUpdating: "Error",
    creating: "creant",
    updating: "actualitzant",
    surveyCreatedUpdated: "Enquesta",
    correctly: "correctament",
    created: "creada",
    updated: "actualitzada",
    redirectingEntities: "Redirigint a la creació d'entitats...",
    titleSpanishRequired: "El títol en espanyol és obligatori",
    titleCatalanRequired: "El títol en català és obligatori",
    descriptionSpanishRequired: "La descripció en espanyol és obligatòria",
    descriptionCatalanRequired: "La descripció en català és obligatòria",
    mustSelectEntity: "Has de seleccionar una entitat",
    goalMustBeGreater: "L'objectiu ha de ser major a 0",
    goalRequired: "L'objectiu és obligatori",
    modifySurveyData: "Modifica les dades de l'enquesta",
    completeSurveyInfo: "Completa la informació per crear una nova enquesta",
    surveyType: "Tipus d'enquesta",
    selectSurveyType: "Selecciona el tipus d'enquesta",
    genericSurveyDescription: "Enquesta genèrica per a totes les entitats",
    entitySurveyDescription: "Enquesta específica per a una entitat",
    genericSurveyCodeInfo: "Es generarà un codi únic per a cada entitat activa en el sistema",
    specificSurveyCodeInfo: "Es generarà un sol codi per a l'entitat seleccionada:",
    selectEntityToSeeCodeInfo: "Selecciona una entitat per veure la informació del codi",
    genericSurveyEditWarning: "Els canvis afectaran a totes les entitats actives en el sistema",
    specificSurveyEditWarning: "Els canvis afectaran a l'entitat seleccionada",
    surveyMustHaveQuestions: "L'enquesta ha de tenir almenys una pregunta",
    genericSurveyOnlyGenericQuestions: "Les enquestes genèriques només poden contenir preguntes genèriques",
    surveyCode: "Codi de l'enquesta",
    surveyCodeActivation: "Activació del codi de l'enquesta",
    surveyCodeActive: "Activat",
    surveyCodeDeActive: "Desactivat",
    titleSpanish: "Escriu un títol en espanyol",
    titleCatalan: "Escriu un títol en català",
    descriptionSpanish: "Escriu una descripció en espanyol",
    descriptionCatalan: "Escriu una descripció en català",
    errorCreatingSurvey: "Error en crear l'enquesta",
    errorUpdatingQuestions:
      "Sin embargo, hubo un problema actualizando algunes preguntes. Per favor, verifica la assignació de preguntes.",
    loadingEntities: "Carregant entitats...",
    loadingUsers: "Carregant usuaris...",

    // Questions
    question: "Pregunta",
    genericShort: "Genèrica",
    specificShort: "Específica",
    surveyId: "Assignar a l'enquesta:",
    typeQuestions: "Tipus de pregunta",
    addQuestion: "Afegir pregunta",
    questionText: "Text de la pregunta",
    audioUrl: "URL d'àudio (opcional)",
    noQuestion: "Aquesta enquesta no té preguntes configurades.",
    questionsSection: "Preguntes de l'enquesta",
    availableQuestions: "Preguntes disponibles",
    selectedQuestions: "Preguntes seleccionades",
    addToSurvey: "Afegir a l'enquesta",
    removeFromSurvey: "Treure de l'enquesta",
    noQuestionsAvailable: "No hi ha preguntes disponibles",
    loadingQuestions: "Carregant preguntes...",
    questionOrder: "Ordre",
    reorderQuestions: "Reordenar preguntes",
    moveUp: "Pujar",
    moveDown: "Baixar",
    manageGenericQuestions:
      "Gestiona les preguntes genèriques que poden usar totes les organitzacions",
    manageSpecificQuestions:
      "Gestiona les preguntes específiques que només poden usar algunes organitzacions",
    aboutSpecificQuestions: "Sobre les preguntes específiques",
    specificQuestionsArePerSurvey:
      "Les preguntes específiques pertanyen a una enquesta concreta.",
    onlyOwnersOrAdminsCanEditSpecific:
      "Només propietaris o administradors poden editar preguntes específiques.",
    noSpecificQuestions: "No hi ha preguntes específiques",
    startAddingFirstSpecificQuestion:
      "Comença creant la primera pregunta específica",
    noGenericQuestions: "No hi ha preguntes genèriques",
    startAddingFirstGenericQuestion:
      "Comença afegint la teva primera pregunta genèrica",
    aboutGenericQuestions: "Sobre les preguntes genèriques",
    genericQuestionsAvailable:
      "Les preguntes genèriques estan disponibles per a totes les organitzacions",
    organizationsCanAdd:
      "Les organitzacions poden afegir aquestes preguntes a les seves enquestes",
    onlyAdminsCanCreateGeneric:
      "Només els administradors poden crear i editar preguntes genèriques",
    eachQuestionMustHaveTranslations:
      "Cada pregunta ha de tenir traducció en espanyol i català",
    canAddAudioForAccessibility:
      "Pots afegir àudio opcional per millorar l'accessibilitat",
    ratingScale: "Escala de valoració",
    audioEnabled: "Àudio: Activat/Desactivat",
    audioEnabledDescription:
      "Activa aquesta opció per habilitar àudio per a aquesta pregunta",
    audioFile: "Pujar fitxer d'àudio",
    audioFileUploaded: "S'ha pujat un fitxer d'àudio personalitzat",
    audioWillBeGenerated:
      "S'es generarà automàticament un àudio per a cada idioma",
    audioFromFile: "Àudio: Fitxer pujat per l'usuari",
    autoGeneratedAudio: "Àudio: Generat automàticament",
    noTextForAudio: "No hi ha text disponible per generar àudio",
    audioPlaybackFailed: "Error en reproduir l'àudio",
    regenerateAudio: "Regenerar àudio",
    audioES: "Àudio en Espanyol",
    audioCA: "Àudio en Català",
    audioEN: "Àudio en Anglès",
    audioAR: "Àudio en Àrab",
    es: "Espanyol",
    ca: "Català",
    en: "Anglès",
    ar: "Àrab",
    english: "Anglès",
    arabic: "Àrab",
    titleEnglish: "Escriu un títol en anglès",
    titleArabic: "Escriu un títol en àrab",
    descriptionEnglish: "Escriu una descripció en anglès",
    descriptionArabic: "Escriu una descripció en àrab",
    generatingAudio: "Generant àudio...",
    generatingAudios: "Generant àudios...",
    preferUploadAudio: "Preferir pujar àudio",
    provideAudioUrl: "Prefereixo proporcionar una URL d'àudio",
    noAudioAvailable: "Àudio no disponible",
    needsRegeneration: "Necessita regeneració",
    play: "Reproduir",
    pause: "Pausar",
    // Codes
    code: "Codi",
    active: "Actiu",
    inactive: "Inactiu",
    activate: "Activar",
    deactivate: "Desactivar",
    copyCode: "Copiar codi",
    manageSurveyCodes: "Gestiona els codis d'accés a les enquestes",
    surveyToggleStatusSuccessfully:
      "Estat de l'enquesta actualitzat correctament",
    errorUpdatingSurveyStatus: "Error en actualitzar l'estat de l'enquesta",
    // Add these to your translations.ca object
    noCodes: "No hi ha codis",
    codesGeneratedAutomatically:
      "Els codis es generen automàticament al crear enquestes",
    accessCodeRules: "Regles dels codis d'accés",
    updatingCode: "Actualitzant el codi d'accés...",
    onlyOneActiveCodePerSurvey: "Només pot haver-hi un codi actiu per enquesta",
    codesGeneratedWhenCreatingSurvey:
      "Els codis es generen automàticament al crear una enquesta",
    canToggleCodesAsNeeded: "Pots activar/desactivar codis segons necessitis",
    inactiveCodesDenyAccess:
      "Els codis inactius no permeten accés a l'enquesta",
    // Export
    exportData: "Exportar dades",
    selectSurvey: "Seleccionar enquesta",
    responseType: "Tipus de resposta",
    childResponse: "Nen/a",
    familyResponse: "Família",
    dateRange: "Rang de dates",
    exportToExcel: "Exportar a Excel",

    // Common
    close: "Tancar",
    save: "Desar",
    saving: "Desant...",
    savingQuestion: "Desant pregunta...",
    cancel: "Cancel·lar",
    edit: "Editar",
    delete: "Eliminar",
    date: "Data",
    createdAt: "Data de creació",
    updatedAt: "Última actualització",
    never: "Mai",
    search: "Buscar",
    searchQuestions: "Cercar preguntes...",
    defaultOrder: "Ordre per defecte",
    newestFirst: "Més recents primer",
    oldestFirst: "Més antigues primer",
    allQuestions: "Totes les preguntes",
    updateAlone: "Actualització",
    creatingAlone: "Creació",
    modifyingData: "Modifica les dades",
    ofThe: "de la",
    the: "del",
    systemUser: "Sistema",
    //Entities
    entities: "Entitats",
    manageEntities: "Gestió d'Entitats i Usuaris",
    manageEntitiesDescription:
      "Administra les organitzacions i usuaris de la plataforma",
    add: "Afegir",
    addEntity: "Nova Entitat",
    editEntity: "Editar entitat",
    deleteEntity: "Eliminar entitat",
    entityName: "Nom de l'entitat",
    entityType: "Tipus d'entitat",
    entityStatus: "Estat de l'entitat",
    users: "Usuaris",
    user: "Usuari",
    manageUsers: "Gestionar usuaris",
    addUser: "Afegir usuari",
    editUser: "Editar usuari",
    deleteUser: "Eliminar usuari",
    userName: "Nom d'usuari",
    userEmail: "Correu electrònic",
    userRole: "Rol d'usuari",
    assignToEntity: "Assignar a entitat",
    removeFromEntity: "Treure d'entitat",
    noEntities: "No hi ha entitats disponibles",
    noUsers: "No hi ha usuaris disponibles",
    confirmDeleteEntity: "Segur que vols eliminar aquesta entitat?",
    confirmDeleteUser: "Segur que vols eliminar aquest usuari?",
    entityCreated: "Entitat creada correctament",
    entityUpdated: "Entitat actualitzada correctament",
    entityDeleted: "Entitat eliminada correctament",
    userCreated: "Usuari creat correctament",
    userUpdated: "Usuari actualitzat correctament",
    userDeleted: "Usuari eliminat correctament",
    contactInfo: "Informació de contacte",
    viewProfile: "Veure perfil",
    describeEntityMission:
      "Descriu breument la missió i activitats de l'entitat",
    phone: "Telèfon",
    address: "Adreça",
    addressPlaceholder: "Carrer, número, ciutat",
    fullName: "Nom complet",
    fullNamePlaceholder: "Noms i cognoms",
    selectRole: "Seleccioneu un rol",
    onlyAdminsCanCreate:
      "Només els administradors poden crear altres administradors",
    selectOrganization: "Seleccionar organització",
    contactEmail: "Correu electrònic de contacte",
    password: "Contrasenya",
    confirmPassword: "Confirmar contrasenya",
    passwordsDoNotMatch: "Les contrasenyes no coincideixen",
    minLength: "Mínim 8 caràcters",
    repeatPassword: "Repetir la contrasenya",
    managementEntities: "Gestió d'Entitats",
    managementUsers: "Gestió d'Usuaris",
    entitiesDescription:
      "Les entitats representen les organitzacions que utilitzen la plataforma",
    usersDescription: "Cada entitat pot tenir múltiples usuaris assignats",
    activeEntitiesDescription:
      "Només les entitats actives poden crear enquestes i accedir a dades",
    contactInfoDescription:
      "La informació de contacte és important per a les comunicacions",
    organizationUsersMustBeAssigned:
      "Els usuaris d'organització han d'estar assignats a una entitat",
    adminsHaveFullAccess:
      "Els administradors tenen accés complet a la plataforma",
    inactiveUsersNoAccess: "Els usuaris inactius no poden accedir al sistema",
    editQuestion: "Editar pregunta",
    newGenericQuestion: "Nova pregunta genèrica",
    newSpecificQuestion: "Nova pregunta específica",
    modifyExistingQuestion: "Modifica la pregunta existent",
    createNewGenericQuestion: "Crea una nova pregunta genèrica",
    createNewSpecificQuestion: "Crea una nova pregunta específica",
    writeQuestionInSpanish: "Escriu la pregunta en espanyol...",
    writeQuestionInCatalan: "Escriu la pregunta en català...",
    audioUrlDescription:
      "URL del fitxer d'àudio per millorar l'accessibilitat (opcional)",
    questionOfOrder: "Ordre de la pregunta",
    questionOrderDescription:
      "Ordre en què apareixerà la pregunta a les enquestes",
    preview: "Vista prèvia",
    noText: "Sense text",
    audioConfigured: "Àudio configurat",
    invalidAudioUrl: "URL d'àudio invàlida",
    audioGenerated: "Àudio generat",
    audioGeneratedSuccessfully:
      "L'àudio s'ha generat correctament des de la URL.",
    couldNotGenerateAudio: "No s'ha pogut generar l'àudio des de la URL.",
    textSpanishRequired: "El text de la pregunta en espanyol és obligatori",
    textCatalanRequired: "El text de la pregunta en català és obligatori",
    textArabicRequired: "El text de la pregunta en àrab és obligatori",
    textEnglishRequired: "El text de la pregunta en anglès és obligatori",
    //filtros
    allStates: "Tots els estats",
    activeFemale: "Activa",
    inactiveFemale: "Inactiva",

    //notifications
    actionCannotBeUndone: "Aquesta acció no es pot desfer.",

    //validations
    nameRequired: "El nom és obligatori",
    emailRequired: "El correu electrònic és obligatori",
    descriptionRequired: "La descripció és obligatòria",
    passwordRequired: "La contrasenya és obligatòria",
    phoneRequired: "El telèfon és obligatori",
    addressRequired: "L'adreça és obligatòria",
    fullNameRequired: "El nom complet és obligatori",
  },
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [language, setLanguage] = useState<"es" | "ca">("es");
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  // Check for existing session on mount
  useEffect(() => {
    const checkSession = () => {
      const savedUser = localStorage.getItem("redmimbre_user");
      const savedLanguage = localStorage.getItem("redmimbre_language");
      const savedToken = localStorage.getItem("redmimbre_token");
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (error) {
          console.error("Error parsing saved user:", error);
          localStorage.removeItem("redmimbre_user");
        }
      }

      if (savedToken) {
        const { role, expired } = decodeToken(savedToken);

        if (expired) {
          logout();
          return;
        }

        setToken(savedToken);
        setRole(role);
      }

      if (savedLanguage && (savedLanguage === "es" || savedLanguage === "ca")) {
        setLanguage(savedLanguage);
      }

      setIsLoading(false);
    };

    checkSession();
  }, []);

  useEffect(() => {
    localStorage.setItem("redmimbre_language", language);
  }, [language]);

  const login = async (email: string, password: string) => {
    const { API_URL } = getConfigs();

    if (!API_URL) {
      throw new Error("API URL not configured");
    }

    try {
      const response = await fetch(`${API_URL}Auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorMessage = await extractDotNetErrorMessage(response);
        throw new Error(errorMessage || "Login failed");
      }

      const data = await response.json();

      setToken(data.token);
      const { role, expired, sub } = decodeToken(data.token);
      setRole(role);
      const userBack = await getInfoUserAuth(data.token);
      if (!userBack || ("error" in userBack && userBack.error)) {
        throw new Error((userBack as unknown as { message: string })?.message || "User not found");
      }
      // Ensure user is of type User before accessing status
      if ("status" in userBack && userBack.status !== 1) {
        throw new Error("User is not active");
      }
      if (
        "organizationId" in userBack &&
        !userBack.organizationId &&
        user?.role === "Organization"
      ) {
        throw new Error("User does not belong to an organization");
      }
      //const mockUser = mockUsers[email.toLowerCase()];
      if (!("error" in userBack)) {
        // Map status from number to string ("active" | "inactive")
        const mappedUser: User = {
          ...userBack,
        };
        setUser(mappedUser);
        localStorage.setItem("redmimbre_user", JSON.stringify(mappedUser));
        localStorage.setItem("redmimbre_token", data?.token);
      }
    } catch (error) {
      console.error("Error during login:", error);
      throw new Error((error as Error).message || "Unexpected error during login");
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("redmimbre_user");
    localStorage.removeItem("redmimbre_token");
  };

  const currentTranslations = translations[language];

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        language,
        setLanguage,
        login,
        logout,
        translations: currentTranslations,
        isLoading,
        role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
